import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  allowedCardActionsRequiredVerificationMethods,
  cardStatuses,
  editableCardStatuses,
} from '../../../utilities/card-utilities';
import { trackCustomEvent } from 'utilities/app-insights';
import Label from 'constants/labels';

/**
 * Cards Action Menu Hook
 *
 * Manages action menu state, modal visibility, and action menu operations.
 * Handles all action menu items (Update PIN, Reset PIN, Transfer, Activate, etc.)
 * and their associated GTM tracking.
 *
 * @param selection - Current selection state from useCardsSelection
 * @param getSelectedCards - Function to get selected card IDs
 * @param getSelectedCardStatuses - Function to get selected card statuses
 * @param getSelectedCardAccounts - Function to get selected card accounts
 * @param getSelectedCardPOSVerificationType - Function to get POS verification types
 * @param isAllSelectedCardsInactive - Function to check if all selected are inactive
 * @param loggedInUser - Current logged in user
 * @param assignedAccountNumber - Assigned account number
 * @param setFilterDisplay - Callback to hide filter when action menu opens
 * @returns Action menu state and operations
 */

export interface ActionMenuState {
  displayActionMenu: boolean;
  displayUpdatePin: boolean;
  displayResetPin: boolean;
  displayTransferCard: boolean;
  displayActivateCard: boolean;
  displayUpdateCardUsage: boolean;
  displayUpdateCardStatus: boolean;
  displayUpdateCardDetails: boolean;
}

export interface ActionMenuOperations {
  actionMenu: ActionMenuState;
  actionMenuItems: any[];
  offsetLeftPosition: number;
  handleActionMenuClick: (route: string, offsetLeftPosition: number) => void;
  closeActionMenuDialog: () => void;
  displayUpdateCardDetailsModal: (enable: boolean) => void;
  closeUpdateCardStatus: () => void;
}

export const useCardsActionMenu = (
  selection: any,
  getSelectedCards: () => string[],
  getSelectedCardStatuses: () => string[],
  getSelectedCardAccounts: () => string[],
  getSelectedCardPOSVerificationType: () => string[],
  isAllSelectedCardsInactive: () => boolean,
  loggedInUser?: string,
  assignedAccountNumber?: string,
  setFilterDisplay?: (display: boolean) => void
): ActionMenuOperations => {
  const [actionMenu, setActionMenu] = useState<ActionMenuState>({
    displayActionMenu: false,
    displayUpdatePin: false,
    displayResetPin: false,
    displayTransferCard: false,
    displayActivateCard: false,
    displayUpdateCardUsage: false,
    displayUpdateCardStatus: false,
    displayUpdateCardDetails: false,
  });

  const [actionMenuItems, setActionMenuItems] = useState<any[]>([]);
  const [offsetLeftPosition, setOffsetLeftPosition] = useState(0);

  // useRef pattern for stable callbacks (prevents infinite loops)
  const trackEventRef = useRef(trackCustomEvent);
  const setFilterDisplayRef = useRef(setFilterDisplay);

  useEffect(() => {
    trackEventRef.current = trackCustomEvent;
    setFilterDisplayRef.current = setFilterDisplay;
  });

  // Get number of selected records
  const getNumberOfSelectedRecords = useCallback((): number => {
    return Object.values(selection).filter((x: any) => x.isSelected).length;
  }, [selection]);

  /**
   * Update action menu items based on selected cards
   * Applies business rules to determine which actions are available
   */
  const updateActionMenuItems = useCallback(() => {
    const isActiveCards = getNumberOfSelectedRecords() > 0;
    const selectedStatuses = getSelectedCardStatuses();
    const isEditable = selectedStatuses.every((status) => editableCardStatuses.includes(status));
    const isCardActionAllowed = getSelectedCardPOSVerificationType().every((method) =>
      allowedCardActionsRequiredVerificationMethods.includes(method)
    );

    const menuItems = [
      {
        displayText: Label.ACTIVATE_CARD,
        route: '/activatecard',
        condition: isAllSelectedCardsInactive() && isEditable,
      },
      {
        displayText: Label.UPDATE_PIN,
        route: '/updatepin',
        condition: isActiveCards && isEditable && isCardActionAllowed,
      },
      {
        displayText: Label.RESET_PIN,
        route: '/resetpin',
        condition: isActiveCards && isEditable && isCardActionAllowed,
      },
      {
        displayText: Label.TRANSFER_CARD,
        route: '/transfercards',
        condition: isActiveCards && isEditable,
      },
      {
        displayText: Label.UPDATE_USAGE,
        route: '/updateusage',
        condition: isActiveCards && getSelectedCardAccounts().length === 1 && isEditable,
      },
      {
        displayText: Label.UPDATE_STATUS,
        route: '/updatestatus',
        condition:
          isActiveCards &&
          selectedStatuses.length === 1 &&
          cardStatuses.find((status) => status.label === selectedStatuses[0])?.allowedUpdateTargets?.length > 0,
      },
    ];

    const filteredMenuItems = menuItems
      .filter((item) => item.condition)
      .map((item) => ({ displayText: item.displayText, route: item.route }));

    const displayActionMenu = filteredMenuItems.length > 0;

    setActionMenuItems(filteredMenuItems);
    setActionMenu((prev) => ({
      ...prev,
      displayActionMenu,
    }));
  }, [
    getNumberOfSelectedRecords,
    getSelectedCardStatuses,
    getSelectedCardAccounts,
    getSelectedCardPOSVerificationType,
    isAllSelectedCardsInactive,
  ]);

  // Update action menu when selection changes
  useEffect(() => {
    updateActionMenuItems();
  }, [selection, updateActionMenuItems]);

  /**
   * Handle action menu item click with GTM tracking
   */
  const handleActionMenuClick = useCallback(
    (route: string, offsetLeft: number) => {
      const selectedCardsList = getSelectedCards();

      // GTM tracking based on route
      switch (route) {
        case '/updatepin':
          window.dataLayer.push({
            event: 'Card_Portal_All_Update_PIN',
            button_click: true,
          });
          trackEventRef.current?.(
            'Toggle Update Pin',
            loggedInUser,
            assignedAccountNumber,
            'Clicked to toggle action menu item on cards list page.',
            { selectedCard: selectedCardsList }
          );
          break;

        case '/resetpin':
          window.dataLayer.push({
            event: 'Card_Portal_All_Reset_PIN_Request',
            button_click: true,
          });
          trackEventRef.current?.(
            'Toggle Reset Pin',
            loggedInUser,
            assignedAccountNumber,
            'Clicked to toggle action menu item on cards list page.',
            { selectedCard: selectedCardsList }
          );
          break;

        case '/transfercards':
          window.dataLayer.push({
            event: 'Card_Portal_All_Transfer_Card',
            button_click: true,
          });
          trackEventRef.current?.(
            'Toggle Transfer Card',
            loggedInUser,
            assignedAccountNumber,
            'Clicked to toggle action menu item on cards list page.',
            { selectedCard: selectedCardsList }
          );
          break;

        case '/activatecard':
          trackEventRef.current?.(
            'Toggle Activate Card',
            loggedInUser,
            assignedAccountNumber,
            'Clicked to toggle action menu item on cards list page.',
            { selectedCard: selectedCardsList }
          );
          break;

        case '/updateusage':
          window.dataLayer.push({
            event: 'Card_Portal_All_Update_Usage',
            button_click: true,
          });
          trackEventRef.current?.(
            'Toggle Update Usage',
            loggedInUser,
            assignedAccountNumber,
            'Clicked to toggle action menu item on cards list page.',
            { selectedCard: selectedCardsList }
          );
          break;

        case '/updatestatus':
          window.dataLayer.push({
            event: 'Card_Portal_All_Update_Status',
            button_click: true,
          });
          trackEventRef.current?.(
            'Toggle Update Status',
            loggedInUser,
            assignedAccountNumber,
            'Clicked to toggle action menu item on cards list page.',
            { selectedCard: selectedCardsList }
          );
          break;
      }

      setOffsetLeftPosition(offsetLeft - 1);
      setFilterDisplayRef.current?.(false);

      // Use functional updater to read current state (prev) instead of
      // stale actionMenu closure — prevents toggle bug where clicking the
      // same action menu item twice wouldn't close it.
      const routeToKey: Record<string, keyof ActionMenuState> = {
        '/updatepin': 'displayUpdatePin',
        '/resetpin': 'displayResetPin',
        '/transfercards': 'displayTransferCard',
        '/activatecard': 'displayActivateCard',
        '/updateusage': 'displayUpdateCardUsage',
        '/updatestatus': 'displayUpdateCardStatus',
      };

      setActionMenu((prev) => {
        const key = routeToKey[route];
        return {
          ...prev,
          displayUpdatePin: false,
          displayResetPin: false,
          displayTransferCard: false,
          displayActivateCard: false,
          displayUpdateCardUsage: false,
          displayUpdateCardStatus: false,
          ...(key ? { [key]: !prev[key] } : {}),
        };
      });
    },
    [getSelectedCards, loggedInUser, assignedAccountNumber]
  );

  // Close action menu dialog
  const closeActionMenuDialog = useCallback(() => {
    trackEventRef.current?.(
      'Close Cards Page Action Menu',
      loggedInUser,
      assignedAccountNumber,
      'Update Pin, Reset Pin, Transfer Card, Activate Card, Update Card Usage or Update Card Status action menu item closed.'
    );

    setActionMenu((prev) => ({
      ...prev,
      displayUpdatePin: false,
      displayResetPin: false,
      displayTransferCard: false,
      displayActivateCard: false,
      displayUpdateCardUsage: false,
      displayUpdateCardStatus: false,
    }));
  }, [loggedInUser, assignedAccountNumber]);

  // Display update card details modal
  const displayUpdateCardDetailsModal = useCallback((enable: boolean) => {
    setActionMenu((prev) => ({
      ...prev,
      displayUpdateCardStatus: enable ? false : prev.displayUpdateCardStatus,
      displayUpdateCardDetails: enable,
    }));
  }, []);

  // Close update card status modal
  const closeUpdateCardStatus = useCallback(() => {
    setActionMenu((prev) => ({
      ...prev,
      displayUpdateCardStatus: false,
    }));
  }, []);

  return {
    actionMenu,
    actionMenuItems,
    offsetLeftPosition,
    handleActionMenuClick,
    closeActionMenuDialog,
    displayUpdateCardDetailsModal,
    closeUpdateCardStatus,
  };
};
